import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import './carousel.css';

const MONTH_IT = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

function calcDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function getUpcomingEvents(events, userLoc, limit = 10) {
  const now = new Date();
  let filtered = events.filter(e => e.start_date && new Date(e.start_date) >= now && e.image_url);

  if (userLoc && filtered.length > 0) {
    filtered = filtered.map(e => ({
      ...e,
      _distance: (e.latitude && e.longitude)
        ? calcDistance(userLoc.lat, userLoc.lng, parseFloat(e.latitude), parseFloat(e.longitude))
        : 9999,
    })).sort((a, b) => {
      const scoreA = a._distance * 0.4 + (new Date(a.start_date) - now) / 86400000 * 0.6;
      const scoreB = b._distance * 0.4 + (new Date(b.start_date) - now) / 86400000 * 0.6;
      return scoreA - scoreB;
    });
  } else {
    filtered.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  }

  return filtered.slice(0, limit);
}

class EventCarousel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      events: [],
      loading: true,
      userLoc: null,
      activeIndex: 0,
    };
    this.trackRef = React.createRef();
    this.autoTimer = null;
  }

  componentDidMount() {
    this.loadEvents();
    this.tryGetLocation();
  }

  componentWillUnmount() {
    clearInterval(this.autoTimer);
  }

  tryGetLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const userLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        this.setState({ userLoc }, () => {
          this.reorderEvents();
          if (this.props.onLocationDetected) {
            this.props.onLocationDetected(userLoc);
          }
        });
      },
      () => { /* silently ignore */ },
      { timeout: 6000 }
    );
  };

  loadEvents = async () => {
    try {
      const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:8001' : '';
      const res = await fetch(`${apiBase}/api/events/`);
      const data = await res.json();
      const events = Array.isArray(data) ? data : (data.results || []);
      this.setState({ events, loading: false }, () => {
        this.startAuto();
      });
    } catch {
      this.setState({ loading: false });
    }
  };

  reorderEvents = () => {
    // triggered when location arrives — just force re-render
    this.setState(s => ({ events: [...s.events] }));
  };

  startAuto = () => {
    clearInterval(this.autoTimer);
    this.autoTimer = setInterval(() => {
      const items = this.getDisplayEvents();
      this.setState(s => ({ activeIndex: (s.activeIndex + 1) % items.length }));
    }, 5000);
  };

  getDisplayEvents = () => getUpcomingEvents(this.state.events, this.state.userLoc, 10);

  goTo = (idx) => {
    clearInterval(this.autoTimer);
    this.setState({ activeIndex: idx }, () => this.startAuto());
  };

  prev = () => {
    const items = this.getDisplayEvents();
    this.goTo((this.state.activeIndex - 1 + items.length) % items.length);
  };

  next = () => {
    const items = this.getDisplayEvents();
    this.goTo((this.state.activeIndex + 1) % items.length);
  };

  formatDate = (str) => {
    if (!str) return '';
    const d = new Date(str);
    return `${d.getDate()} ${MONTH_IT[d.getMonth()]}`;
  };

  getCategoryName = (event) => {
    if (event.category?.name) return event.category.name;
    return event.category_name || '';
  };

  render() {
    const { loading } = this.state;
    if (loading) return null;

    const items = this.getDisplayEvents();
    if (items.length === 0) return null;

    const active = items[this.state.activeIndex];
    const prev1 = items[(this.state.activeIndex - 1 + items.length) % items.length];
    const next1 = items[(this.state.activeIndex + 1) % items.length];

    return (
      <div className="event-showcase">
        <div className="event-showcase-inner container">
          <div className="showcase-header">
            <h2 className="showcase-title">
              {this.state.userLoc ? '📍 Vicino a te' : '🔥 In arrivo'}
            </h2>
            <div className="showcase-nav">
              <button className="showcase-arrow" onClick={this.prev} aria-label="Precedente">
                <ChevronLeft size={20} />
              </button>
              <button className="showcase-arrow" onClick={this.next} aria-label="Successivo">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          <div className="showcase-stage">
            {/* Side prev */}
            <div className="showcase-side showcase-side--prev" onClick={this.prev}>
              <div className="showcase-side-card">
                <img src={prev1.image_url} alt={prev1.title} />
                <div className="showcase-side-overlay" />
              </div>
            </div>

            {/* Main card */}
            <Link
              to={`/event/${active.slug || active.id}`}
              className="showcase-main text-decoration-none"
            >
              <div className="showcase-main-img">
                <img src={active.image_url} alt={active.title} />
                <div className="showcase-main-overlay" />
              </div>
              <div className="showcase-main-info">
                {this.getCategoryName(active) && (
                  <span className="showcase-category">{this.getCategoryName(active)}</span>
                )}
                <h3 className="showcase-event-title">{active.title}</h3>
                <div className="showcase-meta">
                  <span className="showcase-date">🗓 {this.formatDate(active.start_date)}</span>
                  {active.location && (
                    <span className="showcase-location">
                      <MapPin size={13} />
                      {active.location.split(',').slice(-2).map(s => s.trim()).join(', ')}
                    </span>
                  )}
                  {active._distance && active._distance < 500 && (
                    <span className="showcase-distance">📍 {Math.round(active._distance)} km</span>
                  )}
                </div>
                <span className="showcase-cta">Scopri l'evento →</span>
              </div>
            </Link>

            {/* Side next */}
            <div className="showcase-side showcase-side--next" onClick={this.next}>
              <div className="showcase-side-card">
                <img src={next1.image_url} alt={next1.title} />
                <div className="showcase-side-overlay" />
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="showcase-dots">
            {items.map((_, i) => (
              <button
                key={i}
                className={`showcase-dot ${i === this.state.activeIndex ? 'active' : ''}`}
                onClick={() => this.goTo(i)}
                aria-label={`Evento ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
}

export default EventCarousel;
